import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import { Radio, Loader2 } from "lucide-react";

const HERO = "https://images.pexels.com/photos/35792107/pexels-photo-35792107.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Authenticated. Welcome to the control room.");
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#0B0D0F] relative overflow-hidden scanline-overlay">
      {/* Visual background elements */}
      <div className="absolute inset-0 dot-grid pointer-events-none opacity-40 z-0" />
      
      {/* Hero */}
      <div className="relative hidden lg:flex flex-col justify-between p-16 z-10 border-r border-[#2C3238]/40">
        <img src={HERO} className="absolute inset-0 w-full h-full object-cover opacity-20 pointer-events-none" alt="" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B0D0F] via-[#0B0D0F]/90 to-transparent" />
        
        <div className="relative flex items-center gap-3">
          <img src="/images/st-logo.png" className="text-[#00E5FF] drop-shadow-[0_0_8px_rgba(0,229,255,0.4)]" height="32" width="64" alt="ST-Scada Logo" />
          <div className="font-heading font-black text-3xl tracking-tight">ST<span className="text-[#00E5FF] text-glow-accent">.SCADA</span></div>
        </div>

        <div className="relative mt-auto">
          <div className="scada-label mb-3 text-[#00E5FF] tracking-[0.25em] text-glow-accent">CONTROL INTERFACE v1.0</div>
          <h1 className="font-heading font-black text-6xl leading-[1.05] tracking-tight max-w-lg">
            Real-time control for physical <span className="text-[#00E5FF] text-glow-accent">systems.</span>
          </h1>
          <p className="text-[#9BA4B5] mt-6 max-w-md text-sm leading-relaxed">
            High-performance telemetry ingestion, custom mimic canvas diagrams, and real-time alert dispatching for modern plant operators.
          </p>
          
          <div className="grid grid-cols-3 gap-3 mt-12 max-w-md">
            {[
              ["1.2M+", "TAGS / DAILY"],
              ["99.999%", "TELEMETRY"],
              ["< 250ms", "LATENCY"],
            ].map(([k, v]) => (
              <div key={v} className="bg-[#14171A]/70 border border-[#2C3238]/60 p-4 backdrop-blur-sm">
                <div className="font-mono-metric text-2xl text-[#E3E8EE] font-semibold">{k}</div>
                <div className="scada-label text-[8px] text-[#5A6573] mt-1.5">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-8 z-10 relative">
        <div className="w-full max-w-md bg-[#14171A]/50 border border-[#2C3238] p-8 backdrop-blur-lg shadow-2xl relative">
          <div className="absolute top-0 left-0 w-2 h-[1px] bg-[#00E5FF]" />
          <div className="absolute top-0 left-0 w-[1px] h-2 bg-[#00E5FF]" />
          <div className="absolute top-0 right-0 w-2 h-[1px] bg-[#00E5FF]" />
          <div className="absolute top-0 right-0 w-[1px] h-2 bg-[#00E5FF]" />
          
          <form onSubmit={submit} className="w-full" data-testid="login-form">
            <div className="scada-label text-[#00E5FF] text-glow-accent mb-2">SECURE CONSOLE GATEWAY</div>
            <h2 className="font-heading font-black text-3xl mb-1 text-[#E3E8EE]">Operator Authentication</h2>
            <p className="text-[#9BA4B5] text-xs mb-8">Authorization required. Unauthorized attempts are logged.</p>

            <div className="space-y-5">
              <div>
                <label className="scada-label block mb-2 text-[#9BA4B5] text-[10px]">Email Address</label>
                <input
                  data-testid="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="name@scada.io"
                  className="w-full border border-[#2C3238]/85 bg-[#0B0D0F]/80 px-4 py-3 text-sm font-mono-metric placeholder-[#5A6573]/60 focus:border-[#00E5FF] transition-all"
                />
              </div>

              <div>
                <label className="scada-label block mb-2 text-[#9BA4B5] text-[10px]">Access Password</label>
                <input
                  data-testid="password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full border border-[#2C3238]/85 bg-[#0B0D0F]/80 px-4 py-3 text-sm font-mono-metric placeholder-[#5A6573]/60 focus:border-[#00E5FF] transition-all"
                />
              </div>

              <button
                type="submit"
                data-testid="login-submit"
                disabled={busy}
                className="w-full bg-[#00E5FF] hover:bg-[#84FFFF] text-[#0B0D0F] font-mono-metric uppercase tracking-[0.2em] font-semibold text-xs py-3.5 transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2 shadow-[0_4px_20px_rgba(0,229,255,0.15)] hover:shadow-[0_4px_24px_rgba(132,255,255,0.3)]"
              >
                {busy && <Loader2 className="animate-spin" size={14} />}
                Authenticate Terminal
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
